/* lp-unlock-attribution — the product page's half of unlock-funnel attribution.

   THE GAP THIS CLOSES. /pages/unlock sends every persona to its own page
   (p1, p2, p3, p5), and those pages stamp the cart with `_cn_lp` at add time
   (assets/lp-buy-box-gifts.js), so their orders can be told apart. Two exits
   go to the plain product page instead — the "Other" ball and the "Skip this,
   just show me the hoodie" link — and nothing there stamped anything. Their
   orders looked exactly like organic product-page traffic, which is why the
   unlock funnel's real conversion could not be read off the order data.

   WHAT IT DOES. The unlock page already puts the choice in the URL
   (`cn_neuro=other` / `cn_neuro=skip`, plus `cn_gifts`) and mirrors it into
   sessionStorage as `cn_flow`. This reads that, and for exactly those two
   handles writes `_cn_lp: unlock-other` or `_cn_lp: unlock-skip` onto the
   cart through /cart/update.js — the same attribute the persona pages use,
   so every existing consumer (order filters, Flow, exports) keeps working.

   THREE RULES.
   1. Never overwrite. A persona page's stamp is the truer signal (the visitor
      saw persona copy and took the gifts); if `_cn_lp` is already set, leave it.
   2. Never relabel a persona. `cn_neuro=adhd` on this page is not ours to
      touch — that visitor wandered over from p1, and p1 will stamp at add.
   3. Never break the page. Every path is wrapped; a failed stamp is a missing
      data point, not a missing sale.

   ONE STAMP PER SESSION. The module runs on every product-page view. Without
   a memo it would GET + POST on each one; `cn_lp_stamped` in sessionStorage
   says it has already happened for this stamp.

   Loaded by sections/lp-buy-box.liquid, so it travels with the buy box —
   the product page and the persona pages alike (where it no-ops, rule 2).
   Pinned in scripts/deploy.mjs LP_CORE_ASSETS so it cannot ship to a 404. */
(() => {
  'use strict';

  const STAMPS = { other: 'unlock-other', skip: 'unlock-skip' };
  const MIRROR_KEY = 'cn_flow';   /* written by lp-neurotype.js remember() */
  const DONE_KEY = 'cn_lp_stamped';

  const readMirror = (storage) => {
    try {
      const raw = storage && storage.getItem(MIRROR_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (e) {
      return null; /* Safari private mode throws; a malformed mirror is just absent */
    }
  };

  /* Which stamp, if any, does this visit deserve. Pure: search string in,
     stamp or null out. The URL wins when it names a handle; the mirror is the
     fallback for a visitor who navigated away and back without the query. */
  const resolveStamp = (search, storage) => {
    let params;
    try {
      params = new URLSearchParams(typeof search === 'string' ? search : '');
    } catch (e) {
      params = new URLSearchParams('');
    }
    const neuro = params.get('cn_neuro');
    const gifts = params.get('cn_gifts');

    if (neuro !== null) {
      if (STAMPS[neuro]) return STAMPS[neuro];
      /* LEGACY SKIP. The skip link used to send gifts with no handle at all;
         a cached copy of the old lp-neurotype.js still arrives that way. */
      return neuro === '' && gifts ? STAMPS.skip : null;
    }
    if (gifts) return STAMPS.skip;

    const mirror = readMirror(storage);
    if (!mirror) return null;
    const mNeuro = typeof mirror.neuro === 'string' ? mirror.neuro : '';
    if (STAMPS[mNeuro]) return STAMPS[mNeuro];
    if (mNeuro === '' && Array.isArray(mirror.gifts) && mirror.gifts.length > 0) return STAMPS.skip;
    return null;
  };

  /* Do the stamping. Returns what happened, as a string, so it can be tested
     and read off the console: noop | already | kept:<existing> |
     stamped:<stamp> | error. Never throws. */
  const apply = async ({ search, storage, fetchFn }) => {
    try {
      const stamp = resolveStamp(search, storage);
      if (!stamp) return 'noop';

      let done = null;
      try {
        done = storage ? storage.getItem(DONE_KEY) : null;
      } catch (e) {
        done = null;
      }
      if (done === stamp) return 'already';

      const mark = () => {
        try {
          if (storage) storage.setItem(DONE_KEY, stamp);
        } catch (e) {
          /* no-op */
        }
      };

      const current = await fetchFn('/cart.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      const cart = current && current.ok ? await current.json() : null;
      const existing = cart && cart.attributes ? cart.attributes._cn_lp : null;
      if (typeof existing === 'string' && existing !== '') {
        mark(); /* rule 1: the persona page got here first */
        return 'kept:' + existing;
      }

      const res = await fetchFn('/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { _cn_lp: stamp } }),
      });
      if (!res || !res.ok) return 'error';
      mark();
      return 'stamped:' + stamp;
    } catch (e) {
      return 'error'; /* rule 3 */
    }
  };

  window.lpUnlockAttribution = { resolveStamp, apply };

  const boot = () => {
    if (typeof window.fetch !== 'function') return;
    let storage = null;
    try {
      storage = window.sessionStorage;
    } catch (e) {
      storage = null;
    }
    apply({ search: window.location.search, storage, fetchFn: window.fetch.bind(window) });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
